import { useSyncExternalStore, type MouseEvent } from 'react';

/**
 * A very small History-API router.
 *
 * The site has a handful of static pages, so a routing library would be more
 * bytes than the pages themselves. The server already falls back to index.html
 * for unknown paths, so deep links work.
 */

/**
 * The room you are seated in. Giving it a real path means the browser's back
 * button behaves, and that leaving the table to browse the site is an ordinary
 * navigation rather than something the app has to forbid.
 */
export const TABLE_PATH = '/table';

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', emit);
}

function currentPath(): string {
  if (typeof window === 'undefined') return '/';
  // Trailing slashes are normalised away so '/switch/' and '/switch' match.
  const path = window.location.pathname.replace(/\/+$/, '');
  return path === '' ? '/' : path;
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (to === window.location.pathname + window.location.search) return;
  if (options.replace) window.history.replaceState({}, '', to);
  else window.history.pushState({}, '', to);
  window.scrollTo(0, 0);
  emit();
}

export function usePath(): string {
  return useSyncExternalStore(subscribe, currentPath, () => '/');
}

/** Intercepts a plain left-click so links navigate without a page load. */
export function linkProps(to: string): {
  href: string;
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
} {
  return {
    href: to,
    onClick: (event) => {
      // Leave modified clicks alone so "open in new tab" still works.
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      navigate(to);
    },
  };
}
